import { useEffect, useMemo, useRef, useState } from 'react';
import { componentSizes } from '@/theme/componentSizes';
import { r } from '@/theme/tokens/radii';
import { Box } from './Box';
import { formFieldLabelSx } from './formFieldStyles';
import {
  resolveFormFieldBackgroundColor,
  type FormFieldBackgroundColor,
  type FormFieldStyleVariant,
} from './formFieldVariants';
import { AddIcon, RemoveIcon } from './icons';
import { Text } from './Text';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ChangeEvent, PointerEvent, ReactElement, ReactNode } from 'react';

export type NumberSpinnerProps = {
  label?: ReactNode;
  value?: string | number | null;
  defaultValue?: number;
  onChange?: (next: string) => void;
  min?: number;
  max?: number;
  step?: number;
  size?: 'small' | 'medium';
  disabled?: boolean;
  error?: boolean;
  placeholder?: string;
  fieldStyle?: FormFieldStyleVariant;
  inputBackgroundColor?: FormFieldBackgroundColor;
  inputHeight?: number;
  sx?: SxProps<Theme>;
};

const parseValue = (value: string | number | null | undefined): number | null => {
  if (value === undefined || value === null || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const clampValue = (value: number, min?: number, max?: number): number => {
  const minClamped = min === undefined ? value : Math.max(min, value);
  return max === undefined ? minClamped : Math.min(max, minClamped);
};

const HALF_STEP_INPUT_PATTERN = /^\d+(\.5?)?$/;
const HOLD_STEP_DELAY_MS = 400;
const HOLD_STEP_INTERVAL_MS = 75;

export const NumberSpinner = ({
  label,
  value,
  defaultValue,
  onChange,
  min,
  max,
  step = 0.5,
  size: _size = 'medium',
  disabled = false,
  error = false,
  placeholder,
  fieldStyle = 'default',
  inputBackgroundColor,
  inputHeight,
  sx,
}: NumberSpinnerProps): ReactElement => {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState<string>(
    defaultValue === undefined ? '' : String(defaultValue),
  );
  const resolvedNumberRef = useRef<number | null>(null);
  const holdTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holdIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const skipClickRef = useRef(false);

  const resolvedValue = isControlled ? (value === null ? '' : String(value)) : internalValue;
  const resolvedNumber = useMemo(() => parseValue(resolvedValue), [resolvedValue]);
  const resolvedInputHeight = inputHeight ?? componentSizes.auth.inputHeight;
  const resolvedInputBackgroundColor = resolveFormFieldBackgroundColor(
    fieldStyle,
    inputBackgroundColor,
  );

  useEffect(() => {
    resolvedNumberRef.current = resolvedNumber;
  }, [resolvedNumber]);

  const applyValue = (next: string): void => {
    if (!isControlled) setInternalValue(next);
    onChange?.(next);
  };

  const applyStep = (direction: 1 | -1): void => {
    const start = resolvedNumberRef.current ?? min ?? 0;
    const next = clampValue(start + direction * step, min, max);
    resolvedNumberRef.current = next;
    applyValue(String(next));
  };

  const stopContinuousStep = (): void => {
    if (holdTimeoutRef.current !== null) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }

    if (holdIntervalRef.current !== null) {
      clearInterval(holdIntervalRef.current);
      holdIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      stopContinuousStep();
    };
  }, []);

  const startContinuousStep = (direction: 1 | -1): void => {
    stopContinuousStep();
    skipClickRef.current = true;
    applyStep(direction);
    holdTimeoutRef.current = setTimeout(() => {
      holdIntervalRef.current = setInterval(() => {
        applyStep(direction);
      }, HOLD_STEP_INTERVAL_MS);
    }, HOLD_STEP_DELAY_MS);
  };

  const handleStepButtonPointerDown =
    (direction: 1 | -1) =>
    (event: PointerEvent<HTMLButtonElement>): void => {
      if (event.pointerType === 'mouse' && event.button !== 0) {
        return;
      }

      startContinuousStep(direction);
    };

  const handleStepButtonClick = (direction: 1 | -1): void => {
    if (skipClickRef.current) {
      skipClickRef.current = false;
      return;
    }

    applyStep(direction);
  };

  const canDecrement = !(disabled || (min !== undefined && (resolvedNumber ?? min) <= min));
  const canIncrement = !(disabled || (max !== undefined && (resolvedNumber ?? max) >= max));

  return (
    <Box sx={sx}>
      <Box sx={{ display: 'grid', gap: '8px' }}>
        {label ? (
          <Text role="body" sx={formFieldLabelSx}>
            {label}
          </Text>
        ) : null}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr auto',
            alignItems: 'stretch',
            minHeight: resolvedInputHeight,
            border: 1,
            borderColor: error ? 'error.main' : 'divider',
            borderRadius: r('sm'),
            backgroundColor: disabled ? 'action.disabledBackground' : resolvedInputBackgroundColor,
            opacity: disabled ? (theme) => theme.app.opacity.disabled : 1,
            overflow: 'hidden',
          }}
        >
          <Box
            component="button"
            type="button"
            aria-label="Decrease value"
            disabled={!canDecrement}
            sx={{
              appearance: 'none',
              borderRadius: 0,
              borderTopLeftRadius: r('sm'),
              borderBottomLeftRadius: r('sm'),
              border: 0,
              margin: 0,
              width: componentSizes.forms.numberSpinnerStepperWidth,
              borderRight: 1,
              borderColor: 'divider',
              backgroundColor: 'transparent',
              color: 'text.secondary',
              display: 'grid',
              placeItems: 'center',
              cursor: canDecrement ? 'pointer' : 'not-allowed',
              '&:disabled': { opacity: 1, color: 'text.disabled' },
            }}
            onClick={() => {
              handleStepButtonClick(-1);
            }}
            onPointerDown={handleStepButtonPointerDown(-1)}
            onPointerLeave={stopContinuousStep}
            onPointerCancel={stopContinuousStep}
            onPointerUp={stopContinuousStep}
          >
            <RemoveIcon sx={{ fontSize: 18 }} />
          </Box>

          <Box
            component="input"
            type="number"
            value={resolvedValue}
            disabled={disabled}
            placeholder={placeholder}
            min={min}
            max={max}
            step={step}
            inputMode="decimal"
            sx={{
              width: '100%',
              border: 0,
              outline: 0,
              textAlign: 'center',
              background: 'transparent',
              color: disabled ? 'text.disabled' : 'text.primary',
              fontSize: (theme) => theme.typography.subtitle1.fontSize,
              lineHeight: (theme) => theme.typography.subtitle1.lineHeight,
              px: '12px',
              '&:disabled': {
                WebkitTextFillColor: 'currentColor',
              },
              '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { display: 'none' },
              MozAppearance: 'textfield',
            }}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const next = event.target.value;
              if (next === '' || HALF_STEP_INPUT_PATTERN.test(next)) {
                applyValue(next);
              }
            }}
          />

          <Box
            component="button"
            type="button"
            aria-label="Increase value"
            disabled={!canIncrement}
            sx={{
              appearance: 'none',
              borderRadius: 0,
              borderTopRightRadius: r('sm'),
              borderBottomRightRadius: r('sm'),
              border: 0,
              margin: 0,
              width: componentSizes.forms.numberSpinnerStepperWidth,
              borderLeft: 1,
              borderColor: 'divider',
              backgroundColor: 'transparent',
              color: 'text.secondary',
              display: 'grid',
              placeItems: 'center',
              cursor: canIncrement ? 'pointer' : 'not-allowed',
              '&:disabled': { opacity: 1, color: 'text.disabled' },
            }}
            onClick={() => {
              handleStepButtonClick(1);
            }}
            onPointerDown={handleStepButtonPointerDown(1)}
            onPointerLeave={stopContinuousStep}
            onPointerCancel={stopContinuousStep}
            onPointerUp={stopContinuousStep}
          >
            <AddIcon sx={{ fontSize: 18 }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export type NumberFieldProps = NumberSpinnerProps;
export const NumberField = NumberSpinner;
